import moment from "moment";

import { BaseAPI } from "./BaseAPI";
import { API_DATE_FORMAT } from "../constants/dateFormat";

import type { APIConf } from "./BaseAPI";
import type {
  BandwidthStat,
  DownloadAdBreakReportParams,
  DownloadAsRunReportParams,
  GetActiveSubscriptionsExportResponse,
  GetActiveSubscriptionsParams,
  GetActiveSubscriptionsResponse,
  GetBandwidthExportResponse,
  GetBandwidthParams,
  GetBandwidthResponse,
  GetContentCountriesExportResponse,
  GetContentCountriesParams,
  GetContentCountriesResponse,
  GetContentMediaKpiResponse,
  GetContentViewsExportResponse,
  GetContentViewsGraphExportResponse,
  GetContentViewsGraphParams,
  GetContentViewsGraphResponse,
  GetContentViewsParams,
  GetContentViewsResponse,
  GetCustomerTransactionsKpiParams,
  GetCustomerTransactionsKpiResponse,
  GetDevicesUsedByContentExportResponse,
  GetDevicesUsedByContentParams,
  GetDevicesUsedByContentResponse,
  GetDevicesUsedExportResponse,
  GetDevicesUsedParams,
  GetDevicesUsedResponse,
  GetInstitutionSessionByDayParams,
  GetInstitutionSessionsParams,
  GetInstitutionViewsParams,
  GetLiveChatParams,
  GetLiveChatResponse,
  GetLiveViewersExportResponse,
  GetLiveViewersParams,
  GetLiveViewersResponse,
  GetMediaDetailedViewsExportResponse,
  GetMediaDetailedViewsParams,
  GetMediaDetailedViewsResponse,
  GetMediaKpiParams,
  GetMediaKpiResponse,
  GetMediasRightsResponse,
  GetOffersPurchasesParams,
  GetOffersPurchasesResponse,
  GetOffersSalesParams,
  GetOffersSalesResponse,
  GetOffersSubscriptionsParams,
  GetOffersSubscriptionsResponse,
  GetOffersTrialUnsubscriptionsParams,
  GetOffersTrialUnsubscriptionsResponse,
  GetOffersTrialUnsubscriptionsViewsParams,
  GetOffersTrialUnsubscriptionsViewsResponse,
  GetOfferUnsubscriptionsGroupByOfferParams,
  GetOfferUnsubscriptionsGroupByOfferResponse,
  GetOfferUnsubscriptionsParams,
  GetOfferUnsubscriptionsResponse,
  GetOfferUserStatsExportResponse,
  GetOfferUserStatsParams,
  GetOfferUserStatsResponse,
  GetProjectCountriesExportResponse,
  GetProjectCountriesParams,
  GetProjectCountriesResponse,
  GetPurchasesTransactionsExportResponse,
  GetPurchasesTransactionsParams,
  GetPurchasesTransactionsResponse,
  GetSignupsAndSubscriptionsExportResponse,
  GetSignupsAndSubscriptionsParams,
  GetSignupsAndSubscriptionsResponse,
  GetSubscriptionsByUserParams,
  GetSubscriptionsByUserResponse,
  GetSumMediaViewsParams,
  GetSumMediaViewsResponse,
  GetUnsubscribeReasonsParams,
  GetUnsubscribeReasonsResponse,
  GetUnsubscriberTransactionsExportResponse,
  GetUnsubscriberTransactionsParams,
  GetUnsubscriberTransactionsResponse,
  GetUserLastWatchedParams,
  GetUserSessionsParams,
  GetUserSessionsResponse,
  GetUserWatchedExportResponse,
  GetUserWatchedGraphExportResponse,
  GetUserWatchedGraphParams,
  GetUserWatchedGraphResponse,
  GetUserWatchedKPIParams,
  GetUserWatchedKPIResponse,
  GetUserWatchedParams,
  GetUserWatchedResponse,
  InstitutionSessionsByDayResponse,
  InstitutionSessionsResponse,
  InstitutionViewsResponse,
} from "./StatsAPI.types";

class StatsAPIClass extends BaseAPI {
  constructor(conf: APIConf) {
    super(conf);
    this.service = "editor_stats";
    this.version = "v1";
  }

  async getUserWatchedKPI({
    userId,
    projectSlug,
  }: GetUserWatchedKPIParams): Promise<GetUserWatchedKPIResponse> {
    return this.axios
      .get<GetUserWatchedKPIResponse>(
        `${this.routeBase}/admin/user/watched_kpi?user_uuid=${userId}`,
        { headers: { "xx-ok-project": projectSlug } },
      )
      .then((response) => response.data);
  }

  getUserWatchedGraph(
    params: GetUserWatchedGraphParams & { isExport: true },
  ): Promise<GetUserWatchedGraphExportResponse>;
  getUserWatchedGraph(
    params: GetUserWatchedGraphParams & { isExport?: false },
  ): Promise<GetUserWatchedGraphResponse>;
  async getUserWatchedGraph({
    userId,
    periodStart,
    periodEnd,
    isExport,
    projectSlug,
  }: GetUserWatchedGraphParams): Promise<
    GetUserWatchedGraphResponse | GetUserWatchedGraphExportResponse
  > {
    const start = moment(periodStart).format(API_DATE_FORMAT);
    const end = moment(periodEnd).format(API_DATE_FORMAT);

    const params: {
      period_start: string;
      period_end: string;
      user_uuid: string;
      export: boolean | null;
    } = {
      period_start: start,
      period_end: end,
      user_uuid: userId,
      export: isExport ?? null,
    };

    return this.axios
      .get<GetUserWatchedGraphResponse | GetUserWatchedGraphExportResponse>(
        `${this.routeBase}/admin/user/watched_medias_graph`,
        {
          params,
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getUserLastWatched({
    userId,
    projectSlug,
  }: GetUserLastWatchedParams): Promise<unknown> {
    return this.axios
      .get(`${this.routeBase}/admin/user/last_watched?user_uuid=${userId}`, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async getUserSessions({
    userId,
    projectSlug,
  }: GetUserSessionsParams): Promise<GetUserSessionsResponse> {
    return this.axios
      .get<GetUserSessionsResponse>(
        `${this.routeBase}/admin/user/sessions?user_uuid=${userId}`,
        { headers: { "xx-ok-project": projectSlug } },
      )
      .then((response) => response.data);
  }

  getDevicesUsed(
    params: GetDevicesUsedParams & { isExport: true },
  ): Promise<GetDevicesUsedExportResponse>;
  getDevicesUsed(
    params: GetDevicesUsedParams & { isExport?: false },
  ): Promise<GetDevicesUsedResponse>;
  async getDevicesUsed({
    startDate,
    endDate,
    isExport = false,
    projectSlug,
  }: GetDevicesUsedParams): Promise<
    GetDevicesUsedResponse | GetDevicesUsedExportResponse
  > {
    const startDateStr = moment(startDate).format(API_DATE_FORMAT);
    const endDateStr = moment(endDate).format(API_DATE_FORMAT);

    const params: {
      period_start: string;
      period_end: string;
      export: boolean | null;
    } = {
      period_start: startDateStr,
      period_end: endDateStr,
      export: isExport || null,
    };

    return this.axios
      .get<GetDevicesUsedResponse | GetDevicesUsedExportResponse>(
        `${this.routeBase}/admin/storefront_project/devices`,
        {
          params,
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  getDevicesUsedByContent(
    params: GetDevicesUsedByContentParams & { isExport: true },
  ): Promise<GetDevicesUsedByContentExportResponse>;
  getDevicesUsedByContent(
    params: GetDevicesUsedByContentParams & { isExport?: false },
  ): Promise<GetDevicesUsedByContentResponse>;
  async getDevicesUsedByContent({
    contentId,
    startDate,
    endDate,
    isExport = false,
    projectSlug,
  }: GetDevicesUsedByContentParams): Promise<
    GetDevicesUsedByContentResponse | GetDevicesUsedByContentExportResponse
  > {
    const startDateStr = moment(startDate).format(API_DATE_FORMAT);
    const endDateStr = moment(endDate).format(API_DATE_FORMAT);

    const params: {
      period_start: string;
      period_end: string;
      export: boolean | null;
    } = {
      period_start: startDateStr,
      period_end: endDateStr,
      export: isExport || null,
    };

    return this.axios
      .get<
        GetDevicesUsedByContentResponse | GetDevicesUsedByContentExportResponse
      >(`${this.routeBase}/admin/content/devices/${contentId}`, {
        params,
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  getUserWatched(
    params: GetUserWatchedParams & { isExport: true },
  ): Promise<GetUserWatchedExportResponse>;
  getUserWatched(
    params: GetUserWatchedParams & { isExport?: false },
  ): Promise<GetUserWatchedResponse>;
  async getUserWatched({
    userId,
    startDate,
    endDate,
    isExport = false,
    projectSlug,
  }: GetUserWatchedParams): Promise<
    GetUserWatchedResponse | GetUserWatchedExportResponse
  > {
    const startDateStr = moment(startDate).format(API_DATE_FORMAT);
    const endDateStr = moment(endDate).format(API_DATE_FORMAT);

    const params: {
      period_start: string;
      period_end: string;
      user_uuid: string;
      export: boolean | null;
    } = {
      period_start: startDateStr,
      period_end: endDateStr,
      user_uuid: userId,
      export: isExport || null,
    };

    return this.axios
      .get<GetUserWatchedResponse | GetUserWatchedExportResponse>(
        `${this.routeBase}/admin/user/watched_medias`,
        {
          params,
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  getOfferUserStats(
    params: GetOfferUserStatsParams & { isExport: true },
  ): Promise<GetOfferUserStatsExportResponse>;
  getOfferUserStats(
    params: GetOfferUserStatsParams & { isExport?: false },
  ): Promise<GetOfferUserStatsResponse>;
  async getOfferUserStats({
    offerId,
    offerPlanType,
    isExport = false,
    projectSlug,
  }: GetOfferUserStatsParams): Promise<
    GetOfferUserStatsResponse | GetOfferUserStatsExportResponse
  > {
    const params: {
      type: string;
      offer_uuid: string;
      export: boolean | null;
    } = {
      type: offerPlanType,
      offer_uuid: offerId,
      export: isExport || null,
    };

    return this.axios
      .get<GetOfferUserStatsResponse | GetOfferUserStatsExportResponse>(
        `${this.routeBase}/admin/offer/users`,
        {
          params,
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getInstitutionSessions({
    institutionUuid,
    projectSlug,
  }: GetInstitutionSessionsParams): Promise<InstitutionSessionsResponse> {
    return this.axios
      .get<InstitutionSessionsResponse>(
        `${this.routeBase}/admin/kpi/edu/sessions`,
        {
          params: {
            user_uuid: institutionUuid,
          },
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getInstitutionViews({
    institutionUuid,
    projectSlug,
  }: GetInstitutionViewsParams): Promise<InstitutionViewsResponse> {
    return this.axios
      .get<InstitutionViewsResponse>(`${this.routeBase}/admin/kpi/edu/views`, {
        params: {
          user_uuid: institutionUuid,
        },
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async getInstitutionSessionByDay({
    institutionUuid,
    projectSlug,
    periodStart,
    periodEnd,
  }: GetInstitutionSessionByDayParams): Promise<InstitutionSessionsByDayResponse> {
    const startDateStr = moment(periodStart).format(API_DATE_FORMAT);
    const endDateStr = moment(periodEnd).format(API_DATE_FORMAT);

    return this.axios
      .get<InstitutionSessionsByDayResponse>(
        `${this.routeBase}/admin/storefront_project/edu`,
        {
          params: {
            user_uuid: institutionUuid,
            period_start: startDateStr,
            period_end: endDateStr,
          },
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  getActiveSubscriptions(
    params: GetActiveSubscriptionsParams & { isExport: true },
  ): Promise<GetActiveSubscriptionsExportResponse>;
  getActiveSubscriptions(
    params: GetActiveSubscriptionsParams & { isExport?: false },
  ): Promise<GetActiveSubscriptionsResponse>;
  async getActiveSubscriptions({
    projectSlug,
    startDate,
    endDate,
    languageUuid,
    isExport = false,
  }: GetActiveSubscriptionsParams): Promise<
    GetActiveSubscriptionsResponse | GetActiveSubscriptionsExportResponse
  > {
    const startDateStr = moment(startDate).format(API_DATE_FORMAT);
    const endDateStr = moment(endDate).format(API_DATE_FORMAT);

    const params: {
      period_start: string;
      period_end: string;
      lang: string;
      export?: boolean;
    } = {
      period_start: startDateStr,
      period_end: endDateStr,
      lang: languageUuid,
    };

    if (isExport) {
      params.export = true;
    }

    return this.axios
      .get<
        GetActiveSubscriptionsResponse | GetActiveSubscriptionsExportResponse
      >(`${this.routeBase}/admin/storefront_project/active_subscriptions`, {
        params,
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  getUnsubscriberTransactions(
    params: GetUnsubscriberTransactionsParams & { isExport: true },
  ): Promise<GetUnsubscriberTransactionsExportResponse>;
  getUnsubscriberTransactions(
    params: GetUnsubscriberTransactionsParams & { isExport?: false },
  ): Promise<GetUnsubscriberTransactionsResponse>;
  async getUnsubscriberTransactions({
    projectSlug,
    startDate,
    endDate,
    languageUuid,
    isExport = false,
  }: GetUnsubscriberTransactionsParams): Promise<
    | GetUnsubscriberTransactionsResponse
    | GetUnsubscriberTransactionsExportResponse
  > {
    const startDateStr = moment(startDate).format(API_DATE_FORMAT);
    const endDateStr = moment(endDate).format(API_DATE_FORMAT);

    const params: {
      period_start: string;
      period_end: string;
      lang: string;
      export?: boolean;
    } = {
      period_start: startDateStr,
      period_end: endDateStr,
      lang: languageUuid,
    };

    if (isExport) {
      params.export = true;
    }

    return this.axios
      .get<
        | GetUnsubscriberTransactionsResponse
        | GetUnsubscriberTransactionsExportResponse
      >(`${this.routeBase}/admin/offer/unsubscriber_transactions`, {
        params,
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  getPurchasesTransactions(
    params: GetPurchasesTransactionsParams & { isExport: true },
  ): Promise<GetPurchasesTransactionsExportResponse>;
  getPurchasesTransactions(
    params: GetPurchasesTransactionsParams & { isExport?: false },
  ): Promise<GetPurchasesTransactionsResponse>;
  async getPurchasesTransactions({
    projectSlug,
    startDate,
    endDate,
    languageUuid,
    isExport = false,
  }: GetPurchasesTransactionsParams): Promise<
    GetPurchasesTransactionsResponse | GetPurchasesTransactionsExportResponse
  > {
    const startDateStr = moment(startDate).format(API_DATE_FORMAT);
    const endDateStr = moment(endDate).format(API_DATE_FORMAT);

    const params: {
      period_start: string;
      period_end: string;
      lang: string;
      export?: boolean;
    } = {
      period_start: startDateStr,
      period_end: endDateStr,
      lang: languageUuid,
    };

    if (isExport) {
      params.export = true;
    }

    return this.axios
      .get<
        | GetPurchasesTransactionsResponse
        | GetPurchasesTransactionsExportResponse
      >(`${this.routeBase}/admin/storefront_project/purchases`, {
        params,
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async getSumMediaViews({
    projectSlug,
    startDate,
    endDate,
    page,
    perPage,
    order,
    search,
    filter,
  }: GetSumMediaViewsParams): Promise<GetSumMediaViewsResponse> {
    const startDateStr = moment(startDate).format(API_DATE_FORMAT);
    const endDateStr = moment(endDate).format(API_DATE_FORMAT);

    return this.axios
      .get<GetSumMediaViewsResponse>(
        `${this.routeBase}/admin/content/sum_media_views`,
        {
          params: {
            period_start: startDateStr,
            period_end: endDateStr,
            page,
            per_page: perPage,
            search: search ?? null,
            ...(order
              ? { column: order.field, direction: order.direction }
              : {}),
            ...filter,
          },
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getOffersPurchases({
    projectSlug,
    startDate,
    endDate,
  }: GetOffersPurchasesParams): Promise<GetOffersPurchasesResponse> {
    const startDateStr = moment(startDate).format(API_DATE_FORMAT);
    const endDateStr = moment(endDate).format(API_DATE_FORMAT);

    const params: {
      period_start: string;
      period_end: string;
    } = {
      period_start: startDateStr,
      period_end: endDateStr,
    };

    return this.axios
      .get<GetOffersPurchasesResponse>(
        `${this.routeBase}/admin/offer/purchases`,
        {
          params,
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getOffersSubscriptions({
    projectSlug,
    startDate,
    endDate,
    offerUuid,
  }: GetOffersSubscriptionsParams): Promise<GetOffersSubscriptionsResponse> {
    if (!startDate || !endDate) {
      throw new Error("startDate and endDate are required");
    }

    const startDateStr = moment(startDate).format(API_DATE_FORMAT);
    const endDateStr = moment(endDate).format(API_DATE_FORMAT);

    const params: {
      period_start: string;
      period_end: string;
      offer_uuid?: string;
    } = {
      period_start: startDateStr,
      period_end: endDateStr,
    };

    if (offerUuid) {
      params.offer_uuid = offerUuid;
    }

    return this.axios
      .get<GetOffersSubscriptionsResponse>(
        `${this.routeBase}/admin/offer/subscriptions`,
        {
          params,
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getOffersSales({
    projectSlug,
  }: GetOffersSalesParams): Promise<GetOffersSalesResponse> {
    return this.axios
      .get<GetOffersSalesResponse>(`${this.routeBase}/admin/kpi/offer_sales`, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async getCustomerTransactionsKpi({
    projectSlug,
  }: GetCustomerTransactionsKpiParams): Promise<GetCustomerTransactionsKpiResponse> {
    return this.axios
      .get<GetCustomerTransactionsKpiResponse>(
        `${this.routeBase}/admin/kpi/customer_transactions`,
        { headers: { "xx-ok-project": projectSlug } },
      )
      .then((response) => response.data);
  }

  async getOffersTrialUnsubscriptions({
    projectSlug,
    startDate,
    endDate,
    isExport = false,
  }: GetOffersTrialUnsubscriptionsParams): Promise<GetOffersTrialUnsubscriptionsResponse> {
    const startDateStr = moment(startDate).format(API_DATE_FORMAT);
    const endDateStr = moment(endDate).format(API_DATE_FORMAT);

    const params: {
      period_start: string;
      period_end: string;
      export?: boolean;
    } = {
      period_start: startDateStr,
      period_end: endDateStr,
    };

    if (isExport) {
      params.export = true;
    }

    return this.axios
      .get<GetOffersTrialUnsubscriptionsResponse>(
        `${this.apiBase}/${this.service}/v2/admin/offer/trial_unsubscriptions`,
        {
          params,
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getOffersTrialUnsubscriptionsViews({
    projectSlug,
    startDate,
    endDate,
    isExport = false,
  }: GetOffersTrialUnsubscriptionsViewsParams): Promise<GetOffersTrialUnsubscriptionsViewsResponse> {
    const startDateStr = moment(startDate).format(API_DATE_FORMAT);
    const endDateStr = moment(endDate).format(API_DATE_FORMAT);

    const params: {
      period_start: string;
      period_end: string;
      export?: boolean;
    } = {
      period_start: startDateStr,
      period_end: endDateStr,
    };

    if (isExport) {
      params.export = true;
    }

    return this.axios
      .get<GetOffersTrialUnsubscriptionsViewsResponse>(
        `${this.apiBase}/${this.service}/v2/admin/offer/trial_unsubscriptions_views`,
        {
          params,
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async downloadAsRunReport({
    channelId,
    scheduleId,
    periodStart,
    periodEnd,
    reportType,
  }: DownloadAsRunReportParams): Promise<Blob> {
    if (!scheduleId || !periodStart || !periodEnd || !reportType) {
      throw new Error(
        "scheduleId, reportType, periodStart and periodEnd are required",
      );
    }
    return this.axios
      .get<Promise<Blob>>(
        `${this.routeBase}/admin/schedule/${channelId}/${scheduleId}/${reportType}`,
        {
          responseType: "blob",
          params: {
            period_start: periodStart,
            period_end: periodEnd,
          },
        },
      )
      .then(async (response) => response.data);
  }

  async downloadAdBreakReport({
    channelId,
    scheduleId,
    periodStart,
    periodEnd,
    reportType,
  }: DownloadAdBreakReportParams): Promise<Blob> {
    return this.axios
      .get<Promise<Blob>>(
        `${this.routeBase}/admin/schedule/${channelId}/${scheduleId}/${reportType}`,
        {
          responseType: "blob",
          params: {
            period_start: periodStart,
            period_end: periodEnd,
          },
        },
      )
      .then(async (response) => response.data);
  }

  getMediaDetailedViews(
    params: GetMediaDetailedViewsParams & { isExport: true },
  ): Promise<GetMediaDetailedViewsExportResponse>;
  getMediaDetailedViews(
    params: GetMediaDetailedViewsParams & { isExport?: false },
  ): Promise<GetMediaDetailedViewsResponse>;
  async getMediaDetailedViews({
    startDate,
    endDate,
    isExport = false,
    projectSlug,
    mediaUuid,
    languageUuid,
  }: GetMediaDetailedViewsParams): Promise<
    GetMediaDetailedViewsResponse | GetMediaDetailedViewsExportResponse
  > {
    const params: {
      period_start: string;
      period_end: string;
      lang: string;
      export?: boolean;
    } = {
      period_start: startDate,
      period_end: endDate,
      lang: languageUuid,
    };

    if (isExport) {
      params.export = true;
    }

    return this.axios
      .get<GetMediaDetailedViewsResponse | GetMediaDetailedViewsExportResponse>(
        `${this.routeBase}/admin/content/media_detailed_views/${mediaUuid}`,
        {
          params,
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getLiveChat({
    projectSlug,
    mediaUuid,
    isExport,
  }: GetLiveChatParams): Promise<GetLiveChatResponse> {
    const params: {
      export?: boolean;
    } = {};

    if (isExport) {
      params.export = true;
    }
    return this.axios
      .get<GetLiveChatResponse>(
        `${this.apiBase}/${this.service}/v2/admin/livechat/${mediaUuid}/export`,
        {
          params,
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  getLiveViewers(
    params: GetLiveViewersParams & { isExport: true },
  ): Promise<GetLiveViewersExportResponse>;
  getLiveViewers(
    params: GetLiveViewersParams & { isExport?: false },
  ): Promise<GetLiveViewersResponse>;
  async getLiveViewers({
    liveUuid,
    projectSlug,
    isExport,
  }: GetLiveViewersParams): Promise<
    GetLiveViewersResponse | GetLiveViewersExportResponse
  > {
    const params: {
      export?: boolean;
    } = {};

    if (isExport) {
      params.export = true;
    }
    return this.axios
      .get<GetLiveViewersResponse | GetLiveViewersExportResponse>(
        `${this.routeBase}/admin/content/live_viewers/${liveUuid}`,
        {
          params,
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getMediaKpi({
    mediaUuid,
    projectSlug,
  }: GetMediaKpiParams): Promise<GetMediaKpiResponse> {
    return this.axios
      .get<GetMediaKpiResponse>(
        `${this.routeBase}/admin/kpi/media/${mediaUuid}`,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getContentMediaKpi({
    projectSlug,
  }: {
    projectSlug: string;
  }): Promise<GetContentMediaKpiResponse> {
    return this.axios
      .get<GetContentMediaKpiResponse>(`${this.routeBase}/admin/kpi/medias`, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  getBandwidth(
    params: GetBandwidthParams & { isExport: true },
  ): Promise<GetBandwidthExportResponse>;
  getBandwidth(
    params: GetBandwidthParams & { isExport?: false },
  ): Promise<GetBandwidthResponse>;
  async getBandwidth({
    startDate,
    endDate,
    isExport = false,
  }: GetBandwidthParams): Promise<
    GetBandwidthResponse | GetBandwidthExportResponse
  > {
    const params: {
      period_start: string;
      period_end: string;
      export: boolean | null;
    } = {
      period_start: moment(startDate).format(API_DATE_FORMAT),
      period_end: moment(endDate).format(API_DATE_FORMAT),
      export: isExport || null,
    };

    return this.axios
      .get<
        GetBandwidthResponse | GetBandwidthExportResponse
      >(`${this.routeBase}/admin/organization/bandwidth`, { params })
      .then((response) => response.data);
  }

  async getCurrentMonthBandwidth(): Promise<BandwidthStat[]> {
    return this.axios
      .get<
        BandwidthStat[]
      >(`${this.routeBase}/admin/organization/bandwidth/month`)
      .then((response) => response.data);
  }

  async getExportMediasRights({
    isExport,
  }: {
    isExport: boolean;
  }): Promise<GetMediasRightsResponse> {
    const params: {
      export: boolean;
    } = {
      export: isExport,
    };
    return this.axios
      .get<GetMediasRightsResponse>(
        `${this.routeBase}/admin/organization/medias/rights`,
        {
          params,
        },
      )
      .then((response) => response.data);
  }

  getSignupsAndSubscriptions(
    params: GetSignupsAndSubscriptionsParams & { isExport: true },
  ): Promise<GetSignupsAndSubscriptionsExportResponse>;
  getSignupsAndSubscriptions(
    params: GetSignupsAndSubscriptionsParams & { isExport?: false },
  ): Promise<GetSignupsAndSubscriptionsResponse>;
  async getSignupsAndSubscriptions({
    projectSlug,
    startDate,
    endDate,
    isExport,
  }: GetSignupsAndSubscriptionsParams): Promise<
    | GetSignupsAndSubscriptionsResponse
    | GetSignupsAndSubscriptionsExportResponse
  > {
    const startDateStr = moment(startDate).format(API_DATE_FORMAT);
    const endDateStr = moment(endDate).format(API_DATE_FORMAT);

    const params: {
      period_start: string;
      period_end: string;
      export?: boolean;
    } = {
      period_start: startDateStr,
      period_end: endDateStr,
    };

    if (isExport) {
      params.export = true;
    }

    return this.axios
      .get<
        | GetSignupsAndSubscriptionsResponse
        | GetSignupsAndSubscriptionsExportResponse
      >(
        `${this.routeBase}/admin/storefront_project/signups_and_subscriptions`,
        {
          params,
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  getContentViews(
    params: GetContentViewsParams & { isExport: true },
  ): Promise<GetContentViewsExportResponse>;
  getContentViews(
    params: GetContentViewsParams & { isExport?: false },
  ): Promise<GetContentViewsResponse>;
  async getContentViews({
    projectSlug,
    startDate,
    endDate,
    isExport,
  }: GetContentViewsParams): Promise<
    GetContentViewsResponse | GetContentViewsExportResponse
  > {
    const startDateStr = moment(startDate).format(API_DATE_FORMAT);
    const endDateStr = moment(endDate).format(API_DATE_FORMAT);

    const params: {
      period_start: string;
      period_end: string;
      export?: boolean;
    } = {
      period_start: startDateStr,
      period_end: endDateStr,
    };

    if (isExport) {
      params.export = true;
    }

    return this.axios
      .get<GetContentViewsResponse | GetContentViewsExportResponse>(
        `${this.routeBase}/admin/storefront_project/views`,
        {
          params,
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  getContentViewsGraph(
    params: GetContentViewsGraphParams & { isExport: true },
  ): Promise<GetContentViewsGraphExportResponse>;
  getContentViewsGraph(
    params: GetContentViewsGraphParams & { isExport?: false },
  ): Promise<GetContentViewsGraphResponse>;
  async getContentViewsGraph({
    contentId,
    startDate,
    endDate,
    isExport,
    projectSlug,
  }: GetContentViewsGraphParams): Promise<
    GetContentViewsGraphResponse | GetContentViewsGraphExportResponse
  > {
    const startDateStr = moment(startDate).format(API_DATE_FORMAT);
    const endDateStr = moment(endDate).format(API_DATE_FORMAT);

    const params: {
      period_start: string;
      period_end: string;
      export?: boolean;
    } = {
      period_start: startDateStr,
      period_end: endDateStr,
    };

    if (isExport) {
      params.export = true;
    }

    return this.axios
      .get<GetContentViewsGraphResponse | GetContentViewsGraphExportResponse>(
        `${this.routeBase}/admin/content/views_graph/${contentId}`,
        {
          params,
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  getContentCountries(
    params: GetContentCountriesParams & { isExport: true },
  ): Promise<GetContentCountriesExportResponse>;
  getContentCountries(
    params: GetContentCountriesParams & { isExport?: false },
  ): Promise<GetContentCountriesResponse>;
  async getContentCountries({
    contentId,
    startDate,
    endDate,
    isExport,
    projectSlug,
  }: GetContentCountriesParams): Promise<
    GetContentCountriesResponse | GetContentCountriesExportResponse
  > {
    const startDateStr = moment(startDate).format(API_DATE_FORMAT);
    const endDateStr = moment(endDate).format(API_DATE_FORMAT);

    const params: {
      period_start: string;
      period_end: string;
      export?: boolean;
    } = {
      period_start: startDateStr,
      period_end: endDateStr,
    };

    if (isExport) {
      params.export = true;
    }

    return this.axios
      .get<GetContentCountriesResponse | GetContentCountriesExportResponse>(
        `${this.routeBase}/admin/content/countries/${contentId}`,
        {
          params,
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  getProjectCountries(
    params: GetProjectCountriesParams & { isExport: true },
  ): Promise<GetProjectCountriesExportResponse>;
  getProjectCountries(
    params: GetProjectCountriesParams & { isExport?: false },
  ): Promise<GetProjectCountriesResponse>;
  async getProjectCountries({
    startDate,
    endDate,
    isExport,
    projectSlug,
  }: GetProjectCountriesParams): Promise<
    GetProjectCountriesResponse | GetProjectCountriesExportResponse
  > {
    const startDateStr = moment(startDate).format(API_DATE_FORMAT);
    const endDateStr = moment(endDate).format(API_DATE_FORMAT);

    const params: {
      period_start: string;
      period_end: string;
      export?: boolean;
    } = {
      period_start: startDateStr,
      period_end: endDateStr,
    };

    if (isExport) {
      params.export = true;
    }

    return this.axios
      .get<GetProjectCountriesResponse | GetProjectCountriesExportResponse>(
        `${this.routeBase}/admin/storefront_project/countries`,
        {
          params,
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getSubscriptionsByUser({
    userUuid,
    projectSlug,
  }: GetSubscriptionsByUserParams): Promise<GetSubscriptionsByUserResponse> {
    return this.axios
      .get<GetSubscriptionsByUserResponse>(
        `${this.routeBase}/admin/user/subscriptions?user_uuid=${userUuid}&period_start=1970-01-01`,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getOfferUnsubscriptions({
    projectSlug,
    startDate,
    endDate,
  }: GetOfferUnsubscriptionsParams): Promise<GetOfferUnsubscriptionsResponse> {
    const startDateStr = moment(startDate).format(API_DATE_FORMAT);
    const endDateStr = moment(endDate).format(API_DATE_FORMAT);

    const params: {
      period_start: string;
      period_end: string;
      regroup_periods: true;
    } = {
      period_start: startDateStr,
      period_end: endDateStr,
      regroup_periods: true,
    };

    return this.axios
      .get<GetOfferUnsubscriptionsResponse>(
        `${this.apiBase}/${this.service}/v2/admin/offer/unsubscriptions`,
        {
          params,
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getOfferUnsubscriptionsGroupByOffer({
    projectSlug,
    startDate,
    endDate,
    offerUuid,
  }: GetOfferUnsubscriptionsGroupByOfferParams): Promise<GetOfferUnsubscriptionsGroupByOfferResponse> {
    const startDateStr = moment(startDate).format(API_DATE_FORMAT);
    const endDateStr = moment(endDate).format(API_DATE_FORMAT);

    const params: {
      period_start: string;
      period_end: string;
      offer_uuid?: string;
      regroup_offers: true;
    } = {
      period_start: startDateStr,
      period_end: endDateStr,
      regroup_offers: true,
    };

    if (offerUuid) {
      params.offer_uuid = offerUuid;
    }

    return this.axios
      .get<GetOfferUnsubscriptionsGroupByOfferResponse>(
        `${this.apiBase}/${this.service}/v2/admin/offer/unsubscriptions`,
        {
          params,
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getUnsubscribeReasons({
    startDate,
    endDate,
    lang,
    projectSlug,
    offerUuid,
  }: GetUnsubscribeReasonsParams): Promise<GetUnsubscribeReasonsResponse> {
    const startDateStr = moment(startDate).format(API_DATE_FORMAT);
    const endDateStr = moment(endDate).format(API_DATE_FORMAT);

    const params: {
      period_start: string;
      period_end: string;
      lang: string;
      offer_uuid?: string;
    } = {
      period_start: startDateStr,
      period_end: endDateStr,
      lang,
    };

    if (offerUuid) {
      params.offer_uuid = offerUuid;
    }

    return this.axios
      .get<GetUnsubscribeReasonsResponse>(
        `${this.routeBase}/admin/offer/unsubscribe_reasons`,
        {
          headers: { "xx-ok-project": projectSlug },
          params,
        },
      )
      .then((response) => response.data);
  }
}

const StatsAPI = new StatsAPIClass({
  token: localStorage.token,
});

export default StatsAPI;
